'use client'

import { BigglesToolResult } from './biggles-tool-result'
import { BigglesMarkdown } from './biggles-markdown'

export interface ToolActivity {
  tool: string
  status: 'calling' | 'done'
  result?: unknown
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  toolActivities?: ToolActivity[]
  isStreaming?: boolean
}

interface BigglesMessageProps {
  message: ChatMessage
}

export function BigglesMessage({ message }: BigglesMessageProps) {
  const isUser = message.role === 'user'

  return (
    // No per-message avatars, still — a decision that outlived its first
    // reason. It was originally forced by the old line-art mark being
    // illegible at 32px; the SOLO helmet has no such floor, but the decision
    // stands on its remaining leg: Claude's own UI carries no per-message
    // avatar either, and the bubble alignment and colour already say who is
    // speaking. Repeating the mascot every turn is noise, not identity.
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      {/* Message content */}
      <div
        className={`flex flex-col gap-1.5 max-w-[80%] ${
          isUser ? 'items-end' : 'items-start'
        }`}
      >
        {/* Tool activities */}
        {message.toolActivities?.map((activity, i) => (
          <div key={i} className="w-full">
            {activity.status === 'calling' && (
              <div className="inline-flex items-center gap-2 text-xs text-muted-foreground py-1.5 px-3 bg-muted rounded-full">
                <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>{getToolLabel(activity.tool)}</span>
              </div>
            )}
            {activity.status === 'done' && activity.result != null && (
              <BigglesToolResult tool={activity.tool} result={activity.result} />
            )}
          </div>
        ))}

        {/* Text content */}
        {message.content && (
          <div
            className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
              isUser
                ? 'bg-primary text-primary-foreground rounded-tr-sm'
                : 'bg-muted text-foreground border border-border rounded-tl-sm'
            }`}
          >
            <div className="break-words">
              {isUser ? (
                <span className="whitespace-pre-wrap">{message.content}</span>
              ) : (
                <BigglesMarkdown text={message.content} />
              )}
              {message.isStreaming && (
                <span className="inline-block w-1.5 h-4 bg-current ml-0.5 animate-pulse rounded-sm" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function getToolLabel(tool: string): string {
  const labels: Record<string, string> = {
    get_my_reservations: 'Checking your schedule...',
    get_aircraft_availability: 'Checking aircraft availability...',
    get_cfi_availability: 'Checking instructor availability...',
    get_my_curriculum_progress: 'Loading curriculum progress...',
    get_fleet_status: 'Checking fleet status...',
    create_reservation: 'Preparing reservation...',
    confirm_action: 'Booking reservation...',
  }
  return labels[tool] || 'Working on it...'
}
